import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  XCircle,
  RefreshCw,
  Calendar,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  FileText,
  Users,
  GraduationCap,
  Loader2,
  Sparkles,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusIcon = (status) => {
  switch (status) {
    case 'excellent':
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    case 'good':
      return <CheckCircle2 className="w-5 h-5 text-teal-500" />;
    case 'attention_needed':
      return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    case 'critical':
      return <XCircle className="w-5 h-5 text-red-500" />;
    default:
      return <AlertCircle className="w-5 h-5 text-slate-400" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'excellent':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'good':
      return 'bg-teal-100 text-teal-700 border-teal-200';
    case 'attention_needed':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'critical':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

const getScoreColor = (score) => {
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-teal-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
};

const getProgressColor = (score) => {
  if (score >= 90) return 'bg-green-500';
  if (score >= 70) return 'bg-teal-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-red-500';
};

const getTrendIcon = (trend) => {
  if (trend === 'improving') return <TrendingDown className="w-4 h-4 text-green-500" />;
  if (trend === 'increasing') return <TrendingUp className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
};

export default function CQCReadiness() {
  const { organization } = useAuth();
  const [activeTab, setActiveTab] = useState('daily');
  const [dailyCheck, setDailyCheck] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingWeekly, setLoadingWeekly] = useState(false);

  useEffect(() => {
    if (organization?.id) {
      fetchDailyCheck();
    }
  }, [organization]);

  const fetchDailyCheck = async () => {
    setLoadingDaily(true);
    try {
      const response = await axios.get(`${API_URL}/api/readiness/daily-check`);
      setDailyCheck(response.data);
    } catch (error) {
      console.error('Error fetching daily check:', error);
      toast.error('Failed to fetch daily check');
    } finally {
      setLoadingDaily(false);
    }
  };

  const fetchWeeklyReport = async () => {
    setLoadingWeekly(true);
    try {
      const response = await axios.get(`${API_URL}/api/readiness/weekly-report`);
      setWeeklyReport(response.data);
    } catch (error) {
      console.error('Error fetching weekly report:', error);
      toast.error('Failed to generate weekly report');
    } finally {
      setLoadingWeekly(false);
    }
  };

  const renderComplianceCard = (title, data, icon) => {
    if (!data) return null;
    
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {icon}
              <CardTitle className="text-base">{title}</CardTitle>
            </div>
            <Badge variant="outline" className={getStatusColor(data.status)}>
              {data.status?.replace('_', ' ')}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Compliance Score</span>
              <span className={`text-2xl font-bold ${getScoreColor(data.score)}`}>{data.score}%</span>
            </div>
            <Progress value={data.score} className={`h-2 ${getProgressColor(data.score)}`} />
            
            {data.issues?.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-slate-700 mb-2">Issues Found:</p>
                <ul className="space-y-1">
                  {data.issues.map((issue, i) => (
                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {data.recommendations?.length > 0 && (
              <div className="pt-2">
                <p className="text-sm font-medium text-slate-700 mb-2">Recommendations:</p>
                <ul className="space-y-1">
                  {data.recommendations.slice(0, 2).map((rec, i) => (
                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen" data-testid="cqc-readiness-page">
      <Header title="CQC Readiness Alerts" />

      <div className="p-6 space-y-6">
        {/* Hero Card */}
        <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-teal-100 flex items-center justify-center">
                <Shield className="w-7 h-7 text-teal-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-teal-900">CQC Inspection Readiness</h2>
                <p className="text-sm text-teal-700 mt-1">
                  Stay inspection-ready with automated daily checks and weekly detailed reports. 
                  Monitor compliance across all CQC domains and receive proactive alerts.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchDailyCheck}
                  disabled={loadingDaily}
                  className="border-teal-300"
                >
                  {loadingDaily ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="daily" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Daily Quick Check
            </TabsTrigger>
            <TabsTrigger value="weekly" className="flex items-center gap-2" onClick={() => !weeklyReport && fetchWeeklyReport()}>
              <Calendar className="w-4 h-4" />
              Weekly Report
            </TabsTrigger>
          </TabsList>

          {/* Daily Check Tab */}
          <TabsContent value="daily" className="space-y-6">
            {loadingDaily ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600 mb-4" />
                  <p className="text-slate-500">Running daily compliance check...</p>
                </CardContent>
              </Card>
            ) : dailyCheck ? (
              <>
                {/* Overall Status */}
                <Card className={`border-2 ${
                  dailyCheck.overall_status === 'excellent' ? 'border-green-200 bg-green-50' :
                  dailyCheck.overall_status === 'good' ? 'border-teal-200 bg-teal-50' :
                  dailyCheck.overall_status === 'attention_needed' ? 'border-amber-200 bg-amber-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {getStatusIcon(dailyCheck.overall_status)}
                        <div>
                          <h3 className="font-semibold text-lg">Today&apos;s Readiness Score</h3>
                          <p className="text-sm text-slate-600">{dailyCheck.quick_summary}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-4xl font-bold ${getScoreColor(dailyCheck.overall_score)}`}>
                          {dailyCheck.overall_score}%
                        </span>
                        <p className="text-sm text-slate-500 mt-1">{dailyCheck.check_date}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Urgent Actions */}
                {dailyCheck.urgent_actions?.length > 0 && (
                  <Card className="border-amber-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Bell className="w-5 h-5 text-amber-500" />
                        Urgent Actions Required
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {dailyCheck.urgent_actions.map((action, i) => (
                          <li key={i} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{action}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Areas Checked */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {dailyCheck.areas_checked && Object.entries(dailyCheck.areas_checked).map(([area, status]) => (
                    <Card key={area} className="text-center">
                      <CardContent className="pt-4 pb-4">
                        {getStatusIcon(status)}
                        <p className="text-sm font-medium mt-2 capitalize">{area.replace('_', ' ')}</p>
                        <Badge variant="outline" className={`mt-1 text-xs ${getStatusColor(status)}`}>
                          {status?.replace('_', ' ')}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Shield className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No daily check data available</p>
                  <Button onClick={fetchDailyCheck} className="mt-4 bg-teal-600 hover:bg-teal-700">
                    Run Daily Check
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Weekly Report Tab */}
          <TabsContent value="weekly" className="space-y-6">
            {loadingWeekly ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600 mb-4" />
                  <p className="text-slate-500">Generating comprehensive weekly report...</p>
                </CardContent>
              </Card>
            ) : weeklyReport ? (
              <>
                {/* Overall Summary */}
                <Card className={`border-2 ${
                  weeklyReport.overall_status === 'excellent' ? 'border-green-200 bg-green-50' :
                  weeklyReport.overall_status === 'good' ? 'border-teal-200 bg-teal-50' :
                  weeklyReport.overall_status === 'attention_needed' ? 'border-amber-200 bg-amber-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {getStatusIcon(weeklyReport.overall_status)}
                        <div>
                          <h3 className="font-semibold text-lg">Weekly Readiness Score</h3>
                          <p className="text-sm text-slate-600">
                            {weeklyReport.report_period?.start} to {weeklyReport.report_period?.end}
                          </p>
                        </div>
                      </div>
                      <span className={`text-4xl font-bold ${getScoreColor(weeklyReport.overall_score)}`}>
                        {weeklyReport.overall_score}%
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Insights */}
                {weeklyReport.ai_insights && (
                  <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-500" />
                        AI Compliance Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-700">{weeklyReport.ai_insights}</p>
                    </CardContent>
                  </Card>
                )}

                {/* CQC Domains */}
                <div>
                  <h3 className="font-semibold mb-4">CQC Domain Scores</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {weeklyReport.cqc_domains && Object.entries(weeklyReport.cqc_domains).map(([key, domain]) => (
                      <Card key={key} className="text-center">
                        <CardContent className="pt-4 pb-4">
                          <span className={`text-2xl font-bold ${getScoreColor(domain.score)}`}>
                            {domain.score}%
                          </span>
                          <p className="text-sm font-medium mt-1">{domain.category}</p>
                          <Badge variant="outline" className={`mt-2 text-xs ${getStatusColor(domain.status)}`}>
                            {domain.status?.replace('_', ' ')}
                          </Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Compliance Areas */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderComplianceCard('Staff Compliance', weeklyReport.staff_compliance, 
                    <Users className="w-5 h-5 text-blue-500" />)}
                  {renderComplianceCard('Documentation', weeklyReport.documentation_compliance,
                    <FileText className="w-5 h-5 text-green-500" />)}
                  {renderComplianceCard('Training', weeklyReport.training_compliance,
                    <GraduationCap className="w-5 h-5 text-purple-500" />)}
                </div>

                {/* Incident Analysis */}
                {weeklyReport.incident_analysis && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        Incident Analysis
                        <div className="ml-auto flex items-center gap-2">
                          {getTrendIcon(weeklyReport.trend_analysis?.incidents_trend)}
                          <span className="text-sm text-slate-500 capitalize">
                            {weeklyReport.trend_analysis?.incidents_trend}
                          </span>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-2xl font-bold">{weeklyReport.incident_analysis.total_this_week}</p>
                          <p className="text-sm text-slate-500">This Week</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-2xl font-bold">{weeklyReport.trend_analysis?.incidents_last_week || 0}</p>
                          <p className="text-sm text-slate-500">Last Week</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-2xl font-bold text-amber-600">{weeklyReport.incident_analysis.open_incidents}</p>
                          <p className="text-sm text-slate-500">Open</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Priority Actions */}
                {weeklyReport.priority_actions?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Priority Action Items</CardTitle>
                      <CardDescription>Actions ranked by importance for CQC compliance</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {weeklyReport.priority_actions.map((item, i) => (
                          <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${
                            item.priority === 'critical' ? 'bg-red-50' :
                            item.priority === 'high' ? 'bg-amber-50' :
                            'bg-slate-50'
                          }`}>
                            <Badge className={
                              item.priority === 'critical' ? 'bg-red-100 text-red-700' :
                              item.priority === 'high' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {item.priority}
                            </Badge>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{item.action}</p>
                              <p className="text-xs text-slate-500">{item.category}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Weekly report not yet generated</p>
                  <Button onClick={fetchWeeklyReport} className="mt-4 bg-teal-600 hover:bg-teal-700">
                    Generate Weekly Report
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
